import { useParams } from "react-router-dom";
import { useCallback, useState } from "react";
import { Typography } from "@mui/material";
import { Edit } from "@mui/icons-material";
import ComponentFormDialog from "features/lightningLanders/components/ComponentFormDialog";
import { saveSharedComponentBlock } from "../services/sharedComponentBlockService";
import produce from "immer";
import { ApiSimpleComponent, ApiComponentBlockCategory } from "@incendium/api";
import { CallbackOrVal } from "Interfaces";

interface ISharedComponentBlockBreadcrumbItemProps {
  component: ApiSimpleComponent | null;
  setComponent: (v: CallbackOrVal<ApiSimpleComponent>) => void;
  setComponents: (v: CallbackOrVal<ApiSimpleComponent[]>) => void;
}

function SharedComponentBlockBreadcrumbItem({ component, setComponent, setComponents }: ISharedComponentBlockBreadcrumbItemProps) {
  const { componentId } = useParams<{ componentId: string }>();
  const [isOpen, setIsOpen] = useState(false);

  const handleClick = useCallback(() => {
    setIsOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const onSaved = useCallback(async (name: string, description: string, category?: ApiComponentBlockCategory) => {
    if (!component) return;

    await saveSharedComponentBlock({
      ...component,
      name,
      description,
      category
    });

    setComponent(component => ({
      ...component,
      name,
      description,
      category
    }));

    setComponents(components => produce(components, draft => {
      // Use externalId for shared blocks, fallback to id for comparison
      const componentKey = component.externalId || component.id?.toString();
      const index = draft.findIndex(t => (t.externalId || t.id?.toString()) === componentKey);
      if (index !== -1) {
        draft[index].name = name;
        draft[index].description = description;
        draft[index].category = category;
      }
    }));

    handleClose();
  }, [component, setComponent, setComponents, handleClose]);

  if (!component) {
    return <>{componentId}</>;
  }

  return (
    <>
      <Typography
        onClick={handleClick}
        color="primary"
        variant="body1"
        noWrap
        sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', zIndex: 1 }}
      >
        {component.name}
        <Edit fontSize="small" sx={{ ml: 0.5 }} />
      </Typography>
      <ComponentFormDialog
        open={isOpen}
        setOpen={setIsOpen}
        onSaved={onSaved}
        name={component.name}
        description={component.description}
        category={component.category}
      />
    </>
  );
}

export default SharedComponentBlockBreadcrumbItem;

